import React, { useMemo, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Modal } from '@amzn/stencil-react-components/modal'

import { DropdownButton } from 'src/components/DropdownButton'
import { LookAndFeelGallery } from 'src/components/LookAndFeelGallery/LookAndFeelGallery'
import { ModalContent } from 'src/components/ModalContent'
import { Features } from 'src/config.app'
import { isFeatureEnabled } from 'src/helper/featureFlagHelper'
import { EDITABLE_ITEM_TYPES, getItemTitle, ItemType } from 'src/models/dto/items/ItemDTO'

export interface AddFromGalleryButtonProps {
    /** Entity id of the module being edited; the picked look-and-feel is inserted as a new page. */
    moduleEntityId: string
    /** Workflow position to insert the new page at; appends to the end when omitted. */
    pageIndex?: number
    editDisabled?: boolean
    variant?: ButtonVariant
    dataTestId?: string
}

/** Item types offered in the gallery picker — the editable item types plus StaticContentLayout. */
const GALLERY_ITEM_TYPES: ItemType[] = Array.from(
    new Set<ItemType>([...EDITABLE_ITEM_TYPES, ItemType.StaticContentLayout])
)

/**
 * A page-level "Add from Gallery" control that sits alongside "Add content" / "Add new page".
 * Clicking it opens a dropdown of item types (mirroring "Add content"); choosing one opens the
 * {@link LookAndFeelGallery} for that item type in a modal. Inserting a look-and-feel adds it as a
 * new page at `pageIndex` and closes the modal.
 */
export const AddFromGalleryButton = ({
    moduleEntityId,
    pageIndex,
    editDisabled,
    variant = ButtonVariant.Secondary,
    dataTestId = 'add-from-gallery-button',
}: AddFromGalleryButtonProps) => {
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null)

    const values = useMemo(
        () =>
            GALLERY_ITEM_TYPES.map((itemType) => ({
                name: getItemTitle(itemType),
                dataTestId: `add-from-gallery-${itemType}-option`,
                onClick: () => setSelectedItemType(itemType),
            })),
        []
    )

    const close = () => setSelectedItemType(null)

    // Hidden in environments where the look-and-feel gallery is not enabled (e.g. prod).
    if (!isFeatureEnabled(Features.LOOK_AND_FEEL_GALLERY)) {
        return null
    }

    return (
        <>
            <DropdownButton
                dataTestId={dataTestId}
                title='Add from Gallery'
                ariaLabel='Add a page from the look-and-feel gallery'
                values={values}
                variant={variant}
                disabled={editDisabled}
            />
            <Modal isOpen={selectedItemType !== null} close={close}>
                <ModalContent
                    titleText='Pick a look-and-feel'
                    maxWidth='1100px'
                    buttons={[
                        <Button key='close-look-and-feel-gallery' onClick={close}>
                            Close
                        </Button>,
                    ]}
                >
                    {selectedItemType && (
                        <LookAndFeelGallery
                            itemType={selectedItemType}
                            moduleEntityId={moduleEntityId}
                            pageIndex={pageIndex}
                            onInserted={close}
                        />
                    )}
                </ModalContent>
            </Modal>
        </>
    )
}
